import React from 'react';
import { toast as message } from 'sonner';
import { getLocalStore, setLocalStore, clearLocalStore } from '../../../utils/common';
import { handleOperationError, insertRecord, updateRecord } from '../../../utils/supabase-operations';
import { getFieldDependentTable } from '../../../utils/metadata';
import { trimEmail, isValidEmail, validateAndNormalizePhone } from '../../../utils/search';
import { renderWithHandlebars } from '../../admin/EmailTemplates/utils/templateRenderer';
import { applyDefaultValues } from '../../../utils/metadata/fields';
import type {
  CRMCellValueChangedEvent,
  GridApiRef
} from '../../../types/grid';
import type { DynamicRecord } from '../../../types';
import type { GridConfiguration } from '../../../types/grid';
import type { ProcessedFieldMetadata } from '../../../types/metadata';

// Type for grid field configurations
type GridFieldConfig = GridConfiguration['ui_data']['fields'][0];

// Parameters for cell value changed handler
export interface CellValueChangedParams {
  tableName: string;
  schemaName: string;
  gridConfig: GridConfiguration;
  fieldData?: ProcessedFieldMetadata | null;
  isClientSideRow: (rowData: DynamicRecord) => boolean;
  clientSideRowsRef: React.MutableRefObject<DynamicRecord[]>;
  gridApiRef: React.MutableRefObject<GridApiRef | null>;
  onWarning?: (title: string, message: string, onConfirm: () => void, onCancel: () => void) => void;
}

/**
 * Creates a cell value changed handler for the TableGrid component
 * 
 * This handler manages both client-side (temporary) rows and server-side updates:
 * - For client-side rows: Updates local state and attempts to save when all required fields are filled
 * - For server-side rows: Immediately updates the database record
 * - Includes error handling with user feedback via toast messages
 * - Supports undo operations for failed updates
 * 
 * @param params - Configuration parameters for the handler
 * @returns Cell value changed event handler function
 */
export function createCellValueChangedHandler(params: CellValueChangedParams) {
  const {
    tableName,
    schemaName,
    gridConfig,
    fieldData,
    isClientSideRow,
    clientSideRowsRef,
    gridApiRef,
    onWarning
  } = params;

  return (event: CRMCellValueChangedEvent) => {
    // Handle async operations in background
    const handleAsyncCellUpdate = async () => {
      const { data, newValue, oldValue } = event;

      // Prevent double edits during undo operations
      const fieldName = event.colDef?.field;
      if (!fieldName) {
        console.warn('No field name available in cell value changed event');
        return;
      }
      const fieldConfig = gridConfig.ui_data.fields.find(f => f.dbCol?.name === fieldName);

      const justDidUndoKey = `justDidUndo_${event.context?.tableName || tableName}`;
      if (getLocalStore(justDidUndoKey)) {
        clearLocalStore(justDidUndoKey);
        return;
      }

      // Check for warning message
      if (!isClientSideRow(data)) {
        const uiData = fieldConfig?.dbCol?.ui_data as any;
        const warningConfig = uiData?.warning_message;

        if (warningConfig?.enabled && onWarning && newValue !== oldValue) {
          try {
            const templateContext = {
              oldValue,
              newValue,
              field_label: fieldConfig?.dbCol?.ui_label || fieldName,
              record: data
            };

            const messageText = await renderWithHandlebars(warningConfig.message, templateContext);
            const titleText = await renderWithHandlebars(warningConfig.title || 'Confirm Change', templateContext);

            // Wait for user confirmation
            await new Promise<void>((resolve, reject) => {
              onWarning(
                titleText,
                messageText,
                () => resolve(), // Confirm
                () => reject(new Error('User cancelled')) // Cancel
              );
            });
          } catch (error) {
            // User cancelled or error occurred
            if ((error as Error).message === 'User cancelled') {
              setLocalStore(justDidUndoKey, true);
              event.api.undoCellEditing();
              return;
            }
            console.error('Error rendering warning:', error);
            // Proceed if rendering error? or block?
            // Blocking for safety
            return;
          }
        }
      }

      if (isClientSideRow(data)) {
        // Update client-side row data
        clientSideRowsRef.current = clientSideRowsRef.current.map((row) =>
          row.id === data.id ? { ...data } : row
        );
        
        const requiredFields = gridConfig?.ui_data?.fields?.filter((field: GridFieldConfig) => field.dbCol?.is_required && !field.dbCol?.is_system);
        const missingRequiredFields = requiredFields?.filter((field: GridFieldConfig) => {
          const fieldName = field.dbCol?.name;
          if (!fieldName) return false; // Skip fields without valid column info
          const value = data[fieldName];
          return value === null || value === undefined || value === '';
        });

        // If all required fields are filled, save the record
        if (missingRequiredFields?.length === 0) {
          try {
            const defaults = fieldData ? applyDefaultValues(tableName, fieldData) : {};
            const insertData = { ...defaults, ...data };
            delete (insertData as any).id;

            // Remove system/audit fields (handled by DB triggers)
            delete (insertData as any).created_at;
            delete (insertData as any).updated_at;
            delete (insertData as any).created_by;
            delete (insertData as any).updated_by;

            // Remove object attributes (relationships and calculated fields)
            // Remove empty strings to avoid type errors on non-text columns
            Object.keys(insertData).forEach((key) => {
              const val = insertData[key];
              if (typeof val === 'object' && val !== null) {
                delete insertData[key];
              } else if (val === '') {
                delete insertData[key];
              }
            });

            // Validate email/phone fields before insert
            const fieldsToValidate = gridConfig?.ui_data?.fields?.filter(
              (field: GridFieldConfig) => field.dbCol?.is_email || field.dbCol?.is_phone
            );

            for (const field of fieldsToValidate || []) {
              const fieldName = field.dbCol?.name;
              if (!fieldName) continue;

              const value = insertData[fieldName];

              if (field.dbCol?.is_email && value) {
                const trimmed = trimEmail(value);
                if (trimmed && !isValidEmail(trimmed)) {
                  message.error(`Invalid email in field ${field.display_name || fieldName}`);
                  return;
                }
                insertData[fieldName] = trimmed;
              }

              if (field.dbCol?.is_phone && value) {
                const result = validateAndNormalizePhone(value);
                if (!result.valid) {
                  message.error(`Invalid phone in field ${field.display_name || fieldName}: ${result.error}`);
                  return;
                }
                // Store as entered (don't normalize)
                insertData[fieldName] = value;
              }
            }

            try {
              const result = await insertRecord({
                tableName,
                schema: schemaName,
                data: insertData,
                options: {
                  invalidateCache: false,
                  returnData: true
                }
              });
              const insertedRowData = Array.isArray(result.data) ? result.data : [result.data];
              const insertedRow = insertedRowData[0];

              if (!insertedRow) {
                console.error('No inserted row data received');
                return;
              }

              clientSideRowsRef.current = clientSideRowsRef.current.filter((row) => row.id !== data.id);
              
              // Fetch full data for the inserted row
              const { data: fullInsertedRow, error: fetchError } = await getFieldDependentTable({
                tableName,
                schema: schemaName,
                conditions: {
                  id: {
                    filterType: 'number',
                    type: 'equals',
                    filter: insertedRow.id,
                    colId: 'id'
                  }
                },
                limitOffset: {
                  limit: 1,
                  offset: 0,
                },
              });

              if (fetchError) {
                console.error(`Error fetching inserted ${tableName}:`, fetchError);
                message.error(`Failed to fetch inserted ${tableName}: ${fetchError.message}`);
              } else {
                // Update grid with real record
                if (gridApiRef.current && fullInsertedRow) {
                  const rowToAdd = Array.isArray(fullInsertedRow) ? fullInsertedRow[0] : fullInsertedRow;
                  gridApiRef.current.api.applyServerSideTransaction({
                    add: [rowToAdd],
                    addIndex: 0,
                    remove: [{ id: data.id }],
                  });
                }
                message.success(`Successfully added new ${tableName}`);
              }
            } catch (insertError) {
              console.error(`Error adding ${tableName}:`, insertError);
              const errorInfo = handleOperationError(insertError, `Failed to add ${tableName}`);
              message.error(errorInfo.userMessage);
            }
          } catch (error) {
            console.error('Error adding new row:', error);
            message.error(`Failed to add ${tableName}: ${(error as Error).message}`);
          }
        }
      } else {
        // Handle existing record update
        let updatedValue = newValue;
        if (typeof newValue === 'object' && newValue !== null && 'table' in newValue) {
          updatedValue = newValue.data.id;
        }
        
        try {
          const result = await updateRecord({
            tableName,
            schema: schemaName,
            data: { [event.colDef.field!]: updatedValue },
            whereClause: { id: data.id },
            options: {
              invalidateCache: false,
              fieldName: event.colDef.field,
              // Compare-and-set against the value this edit started from, so a cell
              // someone else changed first is rejected instead of overwritten.
              expectedValues: { [event.colDef.field!]: oldValue ?? null },
              expectedValueTypes: { [event.colDef.field!]: fieldConfig?.dbCol?.data_type }
            }
          });
          // updateRecord catches its own errors and returns { error } rather
          // than throwing — without this check (previously missing entirely)
          // a blocked or failed update looked identical to a successful one:
          // the cell keeps its edited value with no error and no revert.
          if (result.error) throw result.error;
          message.success('Saved');
        } catch (error) {
          console.error(`Error updating ${tableName}:`, error);
          const errorInfo = handleOperationError(error, `Failed to update ${tableName}`);
          message.error(errorInfo.userMessage);
          setLocalStore(justDidUndoKey, true);
          event.api.undoCellEditing();
        }
      }
    };

    // Execute async operations in background
    handleAsyncCellUpdate().catch(console.error);
  };
}
